export type DemoOwaspStatus = 'ok' | 'warn' | 'critical' | 'planned';

export type DemoOwaspControl = {
  control_key: string;
  name: string;
  status: DemoOwaspStatus;
  enforced: boolean;
  enforcement_available: boolean;
  recommended_action: string;
  last_checked_at: string;
  evidence?: string;
};

export type DemoOwaspCategory = {
  id: string;
  name: string;
  description: string;
  status: DemoOwaspStatus;
  controls: DemoOwaspControl[];
  last_checked_at: string;
};

export type DemoDomain = {
  site_id: string;
  domain: string;
  label: string;
  industry: string;
  last_seen_at: string;
  health_status: 'healthy' | 'attention' | 'high_risk' | 'monitoring_planned';
  policy_tier: 'Monitor' | 'Hardened' | 'Managed';
  issues_requiring_action: number;
  containment_state: 'None in last 30 days' | 'Used recently' | 'Active';
  owasp: DemoOwaspCategory[];
};

export type DemoDomainLog = {
  id: string;
  site_id: string;
  domain: string;
  created_at: string;
  category: 'owasp' | 'auth' | 'plugin' | 'system';
  event_type: string;
  severity: 'info' | 'low' | 'medium' | 'warning' | 'high' | 'critical';
  summary?: string;
  owasp_id?: string;
  control_key?: string;
  status?: 'ok' | 'warn' | 'critical' | 'resolved';
  enforced?: boolean;
  recommended_action?: string;
  evidence?: Record<string, unknown>;
};

export type DemoEvidenceExport = {
  demo: true;
  generated_for: string;
  generated_at: string;
  domain: {
    site_id: string;
    domain: string;
    label: string;
    policy_tier: DemoDomain['policy_tier'];
  };
  owasp_snapshot: DemoOwaspCategory[];
  events: DemoDomainLog[];
  policy: {
    tier: DemoDomain['policy_tier'];
    enforcement: Record<string, boolean>;
  };
};

export const OWASP_2025_CATEGORIES: Array<{
  id: string;
  name: string;
  description: string;
}> = [
  { id: 'A01', name: 'Broken Access Control', description: 'Access control enforcement and detection.' },
  { id: 'A02', name: 'Cryptographic Failures', description: 'Transport and crypto posture signals.' },
  { id: 'A03', name: 'Injection', description: 'Input handling and injection signals.' },
  { id: 'A04', name: 'Insecure Design', description: 'Design safeguards and guardrails.' },
  { id: 'A05', name: 'Security Misconfiguration', description: 'Configuration hygiene and exposure.' },
  { id: 'A06', name: 'Vulnerable and Outdated Components', description: 'Core/plugin update posture.' },
  { id: 'A07', name: 'Identification and Authentication Failures', description: 'Auth hardening and login safety.' },
  { id: 'A08', name: 'Software and Data Integrity Failures', description: 'Integrity checks and update trust.' },
  { id: 'A09', name: 'Security Logging and Monitoring Failures', description: 'Telemetry and alerting readiness.' },
  { id: 'A10', name: 'Server-Side Request Forgery', description: 'Outbound request controls.' },
];

const minutesAgo = (minutes: number) => new Date(Date.now() - minutes * 60 * 1000).toISOString();
const hoursAgo = (hours: number) => new Date(Date.now() - hours * 60 * 60 * 1000).toISOString();
const daysAgo = (days: number) => new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();

const buildCategory = (
  id: string,
  name: string,
  description: string,
  status: DemoOwaspStatus,
  controls: DemoOwaspControl[],
): DemoOwaspCategory => {
  const lastCheckedAt = controls.reduce((latest, control) =>
    control.last_checked_at > latest ? control.last_checked_at : latest,
  controls[0]?.last_checked_at || minutesAgo(90));
  return {
    id,
    name,
    description,
    status,
    controls,
    last_checked_at: lastCheckedAt,
  };
};

const makeControl = (
  control_key: string,
  name: string,
  status: DemoOwaspStatus,
  recommended_action: string,
  enforcement_available = false,
  enforced = false,
  lastCheckedAt = minutesAgo(90),
  evidence?: string,
): DemoOwaspControl => ({
  control_key,
  name,
  status,
  enforced,
  enforcement_available,
  recommended_action,
  last_checked_at: lastCheckedAt,
  evidence,
});

const bakeryOwasp = (): DemoOwaspCategory[] => {
  const categories = new Map(OWASP_2025_CATEGORIES.map((c) => [c.id, c]));
  return [
    buildCategory(
      'A01',
      categories.get('A01')!.name,
      categories.get('A01')!.description,
      'warn',
      [
        makeControl(
          'a01_privilege_escalation_detection',
          'Privilege escalation detection',
          'ok',
          'Review recent role changes for unexpected admin access.',
        ),
        makeControl(
          'a01_rest_user_enum_detected',
          'REST user enumeration exposure',
          'warn',
          'Restrict unauthenticated access to /wp/v2/users.',
          true,
          false,
          minutesAgo(130),
          'Unauthenticated user list route detected.',
        ),
        makeControl(
          'a01_admin_count_baseline',
          'Admin role count baseline',
          'warn',
          'Reduce admin accounts to 2 or fewer.',
          false,
          false,
          minutesAgo(180),
        ),
      ],
    ),
    buildCategory(
      'A06',
      categories.get('A06')!.name,
      categories.get('A06')!.description,
      'critical',
      [
        makeControl(
          'a06_outdated_core',
          'Core version behind',
          'warn',
          'Update WordPress core to the latest security release.',
        ),
        makeControl(
          'a06_vulnerable_plugin_detected',
          'Known vulnerable plugin',
          'critical',
          'Update or remove the vulnerable plugin (demo-simulated).',
          false,
          false,
          minutesAgo(210),
          'Plugin wp-bakery-lite v2.1 flagged by vulnerability feed.',
        ),
        makeControl(
          'a06_outdated_plugins',
          'Outdated plugins detected',
          'warn',
          'Review and update 3 outdated plugins.',
          false,
          false,
          minutesAgo(200),
        ),
      ],
    ),
    buildCategory(
      'A07',
      categories.get('A07')!.name,
      categories.get('A07')!.description,
      'warn',
      [
        makeControl(
          'a07_default_admin_username',
          'Default admin username present',
          'warn',
          'Rename or remove the admin username.',
        ),
        makeControl(
          'a07_failed_login_burst',
          'Failed login burst detected',
          'warn',
          'Enable login throttling and review IP activity.',
          true,
          false,
          minutesAgo(70),
        ),
        makeControl(
          'a07_mfa_visibility',
          'MFA coverage unknown',
          'planned',
          'Confirm MFA is enabled for all administrators.',
        ),
      ],
    ),
    buildCategory(
      'A02',
      categories.get('A02')!.name,
      categories.get('A02')!.description,
      'ok',
      [
        makeControl('a02_https_enforced', 'HTTPS enforced', 'ok', 'No action needed.'),
        makeControl('a02_hsts_missing', 'HSTS policy', 'warn', 'Add HSTS policy in web server configuration.'),
        makeControl('a02_cookie_secure', 'Secure cookies', 'ok', 'No action needed.'),
      ],
    ),
    buildCategory(
      'A03',
      categories.get('A03')!.name,
      categories.get('A03')!.description,
      'ok',
      [
        makeControl('a03_debug_display', 'Debug display disabled', 'ok', 'No action needed.'),
        makeControl('a03_waf_signal', 'Injection signals', 'ok', 'No action needed.'),
        makeControl('a03_input_validation', 'Input validation', 'ok', 'No action needed.'),
      ],
    ),
    buildCategory(
      'A04',
      categories.get('A04')!.name,
      categories.get('A04')!.description,
      'ok',
      [
        makeControl('a04_rate_limit_present', 'Rate limits enabled', 'ok', 'No action needed.'),
        makeControl('a04_nonce_checks', 'Nonce checks verified', 'ok', 'No action needed.'),
        makeControl('a04_break_glass_ttl', 'Break-glass TTL within policy', 'ok', 'No action needed.'),
      ],
    ),
    buildCategory(
      'A05',
      categories.get('A05')!.name,
      categories.get('A05')!.description,
      'warn',
      [
        makeControl('a05_file_editor_enabled', 'File editor enabled', 'warn', 'Set DISALLOW_FILE_EDIT to true.'),
        makeControl('a05_debug_enabled', 'Debug enabled', 'warn', 'Disable WP_DEBUG in production.'),
        makeControl('a05_rest_user_enum', 'REST user enum exposed', 'warn', 'Restrict REST user endpoints.'),
      ],
    ),
    buildCategory(
      'A08',
      categories.get('A08')!.name,
      categories.get('A08')!.description,
      'ok',
      [
        makeControl('a08_integrity_check', 'Integrity baseline', 'ok', 'No action needed.'),
        makeControl('a08_unsigned_commands', 'Signed commands', 'ok', 'No action needed.', true, true),
        makeControl('a08_update_source', 'Update source verified', 'ok', 'No action needed.'),
      ],
    ),
    buildCategory(
      'A09',
      categories.get('A09')!.name,
      categories.get('A09')!.description,
      'ok',
      [
        makeControl('a09_logging_pipeline', 'Logging pipeline active', 'ok', 'No action needed.'),
        makeControl('a09_admin_audit', 'Admin audit coverage', 'ok', 'No action needed.'),
        makeControl('a09_alert_channels', 'Alert channels configured', 'ok', 'No action needed.'),
      ],
    ),
    buildCategory(
      'A10',
      categories.get('A10')!.name,
      categories.get('A10')!.description,
      'ok',
      [
        makeControl('a10_outbound_allowlist', 'Outbound allowlist', 'ok', 'No action needed.', true, true),
        makeControl('a10_proxy_endpoints', 'Proxy endpoints', 'ok', 'No action needed.'),
        makeControl('a10_metadata_access', 'Metadata access', 'planned', 'Platform check scheduled.'),
      ],
    ),
  ];
};

const agencyOwasp = (): DemoOwaspCategory[] => {
  const categories = new Map(OWASP_2025_CATEGORIES.map((c) => [c.id, c]));
  return [
    buildCategory(
      'A06',
      categories.get('A06')!.name,
      categories.get('A06')!.description,
      'warn',
      [
        makeControl('a06_outdated_core', 'Core version behind', 'ok', 'No action needed.'),
        makeControl('a06_outdated_plugins', 'Outdated plugins detected', 'warn', 'Update 2 plugins pending security patches.'),
        makeControl('a06_vulnerability_feed', 'Known vulnerabilities', 'ok', 'No action needed.'),
      ],
    ),
    buildCategory(
      'A05',
      categories.get('A05')!.name,
      categories.get('A05')!.description,
      'warn',
      [
        makeControl('a05_debug_enabled', 'Debug enabled', 'warn', 'Disable WP_DEBUG in production.'),
        makeControl('a05_file_editor_enabled', 'File editor enabled', 'warn', 'Set DISALLOW_FILE_EDIT to true.'),
        makeControl('a05_exposed_version', 'Version exposed', 'ok', 'No action needed.'),
      ],
    ),
    buildCategory(
      'A09',
      categories.get('A09')!.name,
      categories.get('A09')!.description,
      'ok',
      [
        makeControl('a09_logging_pipeline', 'Logging pipeline active', 'ok', 'No action needed.'),
        makeControl('a09_alert_channels', 'Alert channels configured', 'ok', 'No action needed.'),
        makeControl('a09_admin_audit', 'Admin audit coverage', 'ok', 'No action needed.'),
      ],
    ),
    buildCategory(
      'A01',
      categories.get('A01')!.name,
      categories.get('A01')!.description,
      'ok',
      [
        makeControl('a01_admin_baseline', 'Admin baseline stable', 'ok', 'No action needed.'),
        makeControl('a01_privilege_escalation_detection', 'Privilege escalation detection', 'ok', 'No action needed.'),
        makeControl('a01_rest_permissions', 'REST permissions', 'ok', 'No action needed.'),
      ],
    ),
    buildCategory(
      'A02',
      categories.get('A02')!.name,
      categories.get('A02')!.description,
      'ok',
      [
        makeControl('a02_https_enforced', 'HTTPS enforced', 'ok', 'No action needed.'),
        makeControl('a02_hsts_missing', 'HSTS policy', 'planned', 'Awaiting server policy update.'),
        makeControl('a02_cookie_secure', 'Secure cookies', 'ok', 'No action needed.'),
      ],
    ),
    buildCategory(
      'A07',
      categories.get('A07')!.name,
      categories.get('A07')!.description,
      'ok',
      [
        makeControl('a07_mfa_visibility', 'MFA coverage verified', 'ok', 'No action needed.'),
        makeControl('a07_failed_login_burst', 'Failed login burst', 'ok', 'No action needed.'),
        makeControl('a07_rate_limit', 'Login rate limit', 'ok', 'No action needed.', true, true),
      ],
    ),
    buildCategory(
      'A08',
      categories.get('A08')!.name,
      categories.get('A08')!.description,
      'ok',
      [
        makeControl('a08_integrity_check', 'Integrity baseline', 'ok', 'No action needed.'),
        makeControl('a08_signed_commands', 'Signed commands', 'ok', 'No action needed.', true, true),
        makeControl('a08_update_source', 'Update source verified', 'ok', 'No action needed.'),
      ],
    ),
    buildCategory(
      'A03',
      categories.get('A03')!.name,
      categories.get('A03')!.description,
      'ok',
      [
        makeControl('a03_debug_display', 'Debug display disabled', 'ok', 'No action needed.'),
        makeControl('a03_waf_signal', 'Injection signals', 'ok', 'No action needed.'),
        makeControl('a03_input_validation', 'Input validation', 'ok', 'No action needed.'),
      ],
    ),
    buildCategory(
      'A04',
      categories.get('A04')!.name,
      categories.get('A04')!.description,
      'ok',
      [
        makeControl('a04_rate_limit_present', 'Rate limits enabled', 'ok', 'No action needed.'),
        makeControl('a04_nonce_checks', 'Nonce checks verified', 'ok', 'No action needed.'),
        makeControl('a04_break_glass_ttl', 'Break-glass TTL within policy', 'ok', 'No action needed.'),
      ],
    ),
    buildCategory(
      'A10',
      categories.get('A10')!.name,
      categories.get('A10')!.description,
      'ok',
      [
        makeControl('a10_outbound_allowlist', 'Outbound allowlist', 'ok', 'No action needed.', true, true),
        makeControl('a10_proxy_endpoints', 'Proxy endpoints', 'ok', 'No action needed.'),
        makeControl('a10_metadata_access', 'Metadata access', 'planned', 'Platform check scheduled.'),
      ],
    ),
  ];
};

const ecommerceOwasp = (): DemoOwaspCategory[] => {
  const categories = new Map(OWASP_2025_CATEGORIES.map((c) => [c.id, c]));
  return [
    buildCategory(
      'A02',
      categories.get('A02')!.name,
      categories.get('A02')!.description,
      'warn',
      [
        makeControl('a02_https_enforced', 'HTTPS enforced', 'warn', 'Force HTTPS on both site and admin URLs.'),
        makeControl('a02_cookie_secure', 'Secure cookies', 'warn', 'Enable HTTPS so cookies can be secure.'),
        makeControl('a02_hsts_missing', 'HSTS policy', 'warn', 'Add HSTS policy to web server config.'),
      ],
    ),
    buildCategory(
      'A08',
      categories.get('A08')!.name,
      categories.get('A08')!.description,
      'critical',
      [
        makeControl(
          'a08_plugin_integrity',
          'Plugin integrity mismatch',
          'critical',
          'Reinstall the affected plugin from a trusted source.',
          false,
          false,
          minutesAgo(150),
          'Checksum mismatch detected for demo-payment-gateway.php',
        ),
        makeControl('a08_signed_commands', 'Signed commands', 'ok', 'No action needed.', true, true),
        makeControl('a08_update_source', 'Update source verified', 'ok', 'No action needed.'),
      ],
    ),
    buildCategory(
      'A09',
      categories.get('A09')!.name,
      categories.get('A09')!.description,
      'warn',
      [
        makeControl('a09_logging_pipeline', 'Logging pipeline delays', 'warn', 'Review connector queue backlog.'),
        makeControl('a09_alert_channels', 'Alert channels configured', 'ok', 'No action needed.'),
        makeControl('a09_admin_audit', 'Admin audit coverage', 'ok', 'No action needed.'),
      ],
    ),
    buildCategory(
      'A06',
      categories.get('A06')!.name,
      categories.get('A06')!.description,
      'warn',
      [
        makeControl('a06_outdated_core', 'Core version behind', 'warn', 'Update WordPress core.'),
        makeControl('a06_outdated_plugins', 'Outdated plugins detected', 'warn', 'Update 4 plugins pending patches.'),
        makeControl('a06_vulnerability_feed', 'Known vulnerabilities', 'ok', 'No action needed.'),
      ],
    ),
    buildCategory(
      'A07',
      categories.get('A07')!.name,
      categories.get('A07')!.description,
      'ok',
      [
        makeControl('a07_failed_login_burst', 'Failed login burst', 'ok', 'No action needed.'),
        makeControl('a07_mfa_visibility', 'MFA coverage verified', 'ok', 'No action needed.'),
        makeControl('a07_rate_limit', 'Login rate limit', 'ok', 'No action needed.', true, true),
      ],
    ),
    buildCategory(
      'A01',
      categories.get('A01')!.name,
      categories.get('A01')!.description,
      'ok',
      [
        makeControl('a01_admin_baseline', 'Admin baseline stable', 'ok', 'No action needed.'),
        makeControl('a01_privilege_escalation_detection', 'Privilege escalation detection', 'ok', 'No action needed.'),
        makeControl('a01_rest_permissions', 'REST permissions', 'ok', 'No action needed.'),
      ],
    ),
    buildCategory(
      'A03',
      categories.get('A03')!.name,
      categories.get('A03')!.description,
      'ok',
      [
        makeControl('a03_debug_display', 'Debug display disabled', 'ok', 'No action needed.'),
        makeControl('a03_waf_signal', 'Injection signals', 'ok', 'No action needed.'),
        makeControl('a03_input_validation', 'Input validation', 'ok', 'No action needed.'),
      ],
    ),
    buildCategory(
      'A04',
      categories.get('A04')!.name,
      categories.get('A04')!.description,
      'ok',
      [
        makeControl('a04_rate_limit_present', 'Rate limits enabled', 'ok', 'No action needed.'),
        makeControl('a04_nonce_checks', 'Nonce checks verified', 'ok', 'No action needed.'),
        makeControl('a04_break_glass_ttl', 'Break-glass TTL within policy', 'ok', 'No action needed.'),
      ],
    ),
    buildCategory(
      'A05',
      categories.get('A05')!.name,
      categories.get('A05')!.description,
      'ok',
      [
        makeControl('a05_file_editor_enabled', 'File editor disabled', 'ok', 'No action needed.'),
        makeControl('a05_debug_enabled', 'Debug disabled', 'ok', 'No action needed.'),
        makeControl('a05_rest_user_enum', 'REST user enum protected', 'ok', 'No action needed.'),
      ],
    ),
    buildCategory(
      'A10',
      categories.get('A10')!.name,
      categories.get('A10')!.description,
      'ok',
      [
        makeControl('a10_outbound_allowlist', 'Outbound allowlist', 'ok', 'No action needed.', true, true),
        makeControl('a10_proxy_endpoints', 'Proxy endpoints', 'ok', 'No action needed.'),
        makeControl('a10_metadata_access', 'Metadata access', 'planned', 'Platform check scheduled.'),
      ],
    ),
  ];
};

export const buildDemoDomains = (): DemoDomain[] => [
  {
    site_id: 'demo_site_001',
    domain: 'demo-bakery.example',
    label: 'Bakery Small Business',
    industry: 'Retail bakery',
    last_seen_at: minutesAgo(22),
    health_status: 'high_risk',
    policy_tier: 'Monitor',
    issues_requiring_action: 3,
    containment_state: 'Used recently',
    owasp: bakeryOwasp(),
  },
  {
    site_id: 'demo_site_002',
    domain: 'demo-agency.example',
    label: 'Agency Portfolio Site',
    industry: 'Creative studio',
    last_seen_at: minutesAgo(11),
    health_status: 'attention',
    policy_tier: 'Hardened',
    issues_requiring_action: 2,
    containment_state: 'None in last 30 days',
    owasp: agencyOwasp(),
  },
  {
    site_id: 'demo_site_003',
    domain: 'demo-ecommerce.example',
    label: 'Ecommerce Store',
    industry: 'Online retail',
    last_seen_at: minutesAgo(7),
    health_status: 'high_risk',
    policy_tier: 'Managed',
    issues_requiring_action: 4,
    containment_state: 'Active',
    owasp: ecommerceOwasp(),
  },
];

const buildLog = (payload: Omit<DemoDomainLog, 'id' | 'created_at'> & { created_at: string }): DemoDomainLog => ({
  id: `${payload.site_id}-${payload.created_at}-${payload.event_type}`,
  created_at: payload.created_at,
  ...payload,
});

export const buildDemoLogs = (domain: DemoDomain): DemoDomainLog[] => {
  const base: DemoDomainLog[] = [];
  const push = (log: DemoDomainLog) => base.push(log);

  push(buildLog({
    site_id: domain.site_id,
    domain: domain.domain,
    created_at: minutesAgo(15),
    category: 'system',
    event_type: 'demo_mode_active',
    severity: 'info',
    summary: 'Demo Mode active for this simulated site.',
  }));

  domain.owasp.forEach((category, index) => {
    const eventTime = hoursAgo(4 + index * 3);
    category.controls.forEach((control, controlIndex) => {
      if (control.status === 'planned') return;
      const status = control.status === 'ok' ? 'resolved' : control.status;
      push(buildLog({
        site_id: domain.site_id,
        domain: domain.domain,
        created_at: new Date(new Date(eventTime).getTime() - controlIndex * 45 * 60 * 1000).toISOString(),
        category: 'owasp',
        event_type: 'owasp_control_status_changed',
        severity: control.status === 'critical' ? 'critical' : control.status === 'warn' ? 'warning' : 'info',
        summary: `${category.id} ${control.name} status ${control.status}.`,
        owasp_id: category.id,
        control_key: control.control_key,
        status,
        enforced: control.enforced,
        recommended_action: control.recommended_action,
        evidence: control.evidence ? { note: control.evidence } : undefined,
      }));
    });
  });

  push(buildLog({
    site_id: domain.site_id,
    domain: domain.domain,
    created_at: daysAgo(1),
    category: 'auth',
    event_type: 'auth_bruteforce_burst',
    severity: 'warning',
    summary: 'Repeated failed logins detected (demo simulation).',
  }));

  push(buildLog({
    site_id: domain.site_id,
    domain: domain.domain,
    created_at: daysAgo(2),
    category: 'plugin',
    event_type: 'plugin_deactivated',
    severity: 'high',
    summary: 'A security plugin was disabled (demo simulation).',
  }));

  push(buildLog({
    site_id: domain.site_id,
    domain: domain.domain,
    created_at: daysAgo(3),
    category: 'system',
    event_type: 'admin_user_created',
    severity: 'high',
    summary: 'New administrator account created (demo simulation).',
  }));

  const additional = Array.from({ length: 24 }, (_, idx) => {
    const created_at = daysAgo(6 - (idx % 6));
    return buildLog({
      site_id: domain.site_id,
      domain: domain.domain,
      created_at,
      category: 'owasp',
      event_type: 'owasp_control_status_changed',
      severity: idx % 7 === 0 ? 'warning' : 'info',
      summary: idx % 5 === 0 ? 'OWASP control status refreshed (demo).' : 'Monitoring heartbeat recorded (demo).',
      owasp_id: domain.owasp[idx % domain.owasp.length]?.id,
      control_key: domain.owasp[idx % domain.owasp.length]?.controls[0]?.control_key,
      status: idx % 7 === 0 ? 'warn' : 'ok',
      enforced: false,
    });
  });

  return [...base, ...additional].sort((a, b) => (a.created_at > b.created_at ? -1 : 1));
};

export const buildDemoEvidenceExport = (
  domain: DemoDomain,
  logs: DemoDomainLog[],
  enforcementState: Record<string, boolean>,
  policyTier: DemoDomain['policy_tier'],
): DemoEvidenceExport => ({
  demo: true,
  generated_for: 'Demo Mode',
  generated_at: new Date().toISOString(),
  domain: {
    site_id: domain.site_id,
    domain: domain.domain,
    label: domain.label,
    policy_tier: policyTier,
  },
  owasp_snapshot: domain.owasp,
  events: logs,
  policy: {
    tier: policyTier,
    enforcement: enforcementState,
  },
});
